import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  idCardAddModalRecoil,
  idCardModifyModalRecoil,
} from '@/atom/ModalAtom';
import {
  idCardListRecoil,
  idCardModifyNumRecoil,
  idCardListLengthRecoil,
} from '@/atom/IdCardAtom';
import { placeListRecoil } from '@/atom/PlaceAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import IdCardAddModal from '@/modal/IdCardAddModal';
import IdCardModifyModal from '@/modal/IdCardModifyModal';
import { IdCardDto, IdCardSearchDto } from '@/model/idCard.dto';
import { PlaceDto, PlaceSearchDto } from '@/model/place.dto';
import ExcelDownloadButton from '@/component/ExcelDownloadButton';

const IdCardManagement = () => {
  const accessToken = sessionStorage.getItem('accessToken');
  const api = useMemo(() => new ApiService(), []);
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [idCardAddModalState, setIdCardAddModalState] =
    useRecoilState(idCardAddModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [idCardModifyModalState, setIdCardModifyModalState] = useRecoilState(
    idCardModifyModalRecoil,
  );
  const [idCardListState, setIdCardListState] =
    useRecoilState(idCardListRecoil);
  const [idCardModifyNumState, setIdCardModifyNumState] = useRecoilState(
    idCardModifyNumRecoil,
  );
  const [idCardListLengthState, setIdCardListLengthState] = useRecoilState(
    idCardListLengthRecoil,
  );
  const [placeListState, setPlaceListState] = useRecoilState(placeListRecoil);

  const [affiliation, setAffiliation] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [grade, setGrade] = useState<string>('');
  const [applicant, setApplicant] = useState<string>('');
  const [certiId, setCertiId] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [idCardAllList, setIdCardAllList] = useState<IdCardDto[]>([]);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const fetchPlaceList = async () => {
    const dto: PlaceSearchDto = {};
    try {
      const response: PlaceDto[] = await api.getPlaceList(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      setPlaceListState(response);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const fetchIdCardList = async () => {
    if (placeListState !== null) {
      const dto: IdCardSearchDto = {
        affiliation,
        department,
        grade,
        applicant,
        certi_id: certiId,
      };
      try {
        const response: IdCardDto[] = await api.getIdCardList(
          String(sessionStorage.getItem('accessToken')),
          dto,
        );
        setIdCardListLengthState(response.length);
        setIdCardAllList(response);
      } catch (err: any) {
        if (err.response.data.statusCode === 401) {
          sessionStorage.clear();
          navigate('/login');
        }
      }
    }
  };

  const filterIdCardList = async () => {
    if (placeListState !== null) {
      const dto: IdCardSearchDto = {
        affiliation,
        department,
        grade,
        applicant,
        certi_id: certiId,
        offset,
        limit,
      };
      try {
        const response: IdCardDto[] = await api.getFilterIdCardList(
          String(sessionStorage.getItem('accessToken')),
          dto,
        );
        const addResponse = response.map((place) => {
          const placeName = handlePlaceIdTransfrom(place.place_id);
          return {
            ...place,
            placeName,
          };
        });
        setIdCardListState(addResponse);
      } catch (err: any) {
        if (err.response.data.statusCode === 401) {
          sessionStorage.clear();
          navigate('/login');
        }
      }
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    fetchIdCardList();
    filterIdCardList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleIdCardModify = (id: number) => {
    setIdCardModifyNumState(id);
    setIdCardModifyModalState(true);
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      idCardListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectModifyNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    if (checkItems.length > 1) {
      setErrorModalText('다중 수정은 불가합니다.');
      return;
    }
    handleIdCardModify(checkItems[0]);
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      await api.delIdCardList(
        String(sessionStorage.getItem('accessToken')),
        checkItems,
      );
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  const handlePlaceIdTransfrom = (placeId: string) => {
    const placeIds = placeId.split(',');
    const transformedNames = placeIds.map((id) => {
      const place = placeListState?.find((place) => place.place_id === id);
      return place ? place.name : id;
    });
    return transformedNames.join(', ');
  };

  useEffect(() => {
    fetchPlaceList();
  }, []);

  useEffect(() => {
    if (placeListState !== null) {
      fetchIdCardList();
      filterIdCardList();
    }
  }, [placeListState]);

  useEffect(() => {
    fetchIdCardList();
    filterIdCardList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="출입신분증 관리" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputArea>
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="소속"
                value={affiliation}
                onChange={(e) => setAffiliation(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="부서"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="직급"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="신청자"
                value={applicant}
                onChange={(e) => setApplicant(e.target.value)}
              />
              <IS.TextInputType1
                style={{ width: 144 }}
                type="text"
                placeholder="출입증 번호"
                value={certiId}
                onChange={(e) => setCertiId(e.target.value)}
              />
            </InputArea>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{idCardListLengthState}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type1
              type="button"
              onClick={() => setIdCardAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type1 type="button" onClick={handleSelectModifyNumCheck}>
              수정
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
            <ExcelDownloadButton
              headers={[
                { label: '출입증 번호', key: 'certi_id' },
                { label: '신청자', key: 'applicant' },
                { label: '출입장소', key: 'placeName' },
                { label: '소속', key: 'affiliation' },
                { label: '부서', key: 'department' },
                { label: '직급', key: 'grade' },
                { label: '핸드폰 번호', key: 'phone' },
                { label: '발급일자', key: 'issue_date' },
                { label: '만료일', key: 'expire_date' },
              ]}
              data={idCardAllList}
              fileName="출입 신분증 목록"
              sheetName="출입 신분증 목록"
            />
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === idCardListState?.length &&
                        idCardListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="./img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>출입증 번호​</th>
                <th>신청자​</th>
                <th>출입장소​</th>
                <th>소속​</th>
                <th>부서</th>
                <th>직급​</th>
                <th>핸드폰 번호​</th>
                <th>발급일자​</th>
                <th>만료일​</th>
              </tr>
            </thead>
            <tbody>
              {idCardListState?.map((idCard: IdCardDto, index) => (
                <tr key={idCard.id}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(idCard.id))
                        }
                        checked={
                          checkItems.includes(Number(idCard.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="./img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td
                    className="button-text"
                    onClick={() => handleIdCardModify(Number(idCard.id))}
                  >
                    {idCard.certi_id === 'N/A' ? '-' : idCard.certi_id}
                  </td>
                  <td
                    className="button-text"
                    onClick={() => handleIdCardModify(Number(idCard.id))}
                  >
                    {idCard.applicant}
                  </td>
                  {/* <td>
                    {idCard.place_id === 'N/A' || idCard.place_id === ''
                      ? '-'
                      : idCard.place_id}
                  </td> */}
                  <td>
                    {idCard.place_id === 'N/A' || idCard.place_id === ''
                      ? '-'
                      : idCard.placeName}
                  </td>
                  <td>
                    {idCard.affiliation === 'N/A' || idCard.affiliation === ''
                      ? '-'
                      : idCard.affiliation}
                  </td>
                  <td>
                    {idCard.department === 'Irregular' ||
                    idCard.department === ''
                      ? '-'
                      : idCard.department}
                  </td>
                  <td>
                    {idCard.grade === 'Irregular' || idCard.grade === ''
                      ? '-'
                      : idCard.grade}
                  </td>
                  <td>{idCard.phone}</td>
                  <td>{idCard.issue_date}</td>
                  <td>{idCard.expire_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {idCardListLengthState !== 0 && (
          <Pagenation
            listAllLength={idCardListLengthState}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {idCardAddModalState && <IdCardAddModal />}
      {idCardModifyModalState && <IdCardModifyModal />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default IdCardManagement;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;
